import { useState, useEffect, useCallback, useRef } from "react";
import UserService from "../service/user.service";
import TokenService from "../service/token.service";
import {
  evaluateSetupStatus,
  getNonAdminOperationalGroups,
} from "./evaluateSetupStatus";

const INITIAL = {
  loading: true,
  groupCreated: false,
  memberAdded: false,
  setupComplete: false,
};

function extractMemberCount(data) {
  if (Array.isArray(data)) return data.length;
  if (Array.isArray(data?.members)) return data.members.length;
  if (Array.isArray(data?.users)) return data.users.length;
  if (typeof data?.count === "number") return data.count;
  return 0;
}

export function useSetupStatus() {
  const [state, setState] = useState(INITIAL);
  const mountedRef = useRef(true);

  const fetchStatus = useCallback(() => {
    const token = TokenService.getToken();
    if (!token) {
      setState({ ...INITIAL, loading: false, setupComplete: true });
      return;
    }
    setState((s) => ({ ...s, loading: true }));
    UserService.getAllUserGroup(token)
      .then(async (res) => {
        if (!mountedRef.current) return;
        const opGroups = getNonAdminOperationalGroups(res.data);
        const memberCounts = {};
        if (opGroups.length > 0) {
          const results = await Promise.allSettled(
            opGroups.map((g) => UserService.getUserGroupMember(token, g._id))
          );
          opGroups.forEach((g, i) => {
            const r = results[i];
            memberCounts[g._id] =
              r.status === "fulfilled" ? extractMemberCount(r.value?.data) : 0;
          });
        }
        if (!mountedRef.current) return;
        const result = evaluateSetupStatus(res.data, memberCounts);
        setState({ loading: false, ...result });
      })
      .catch(() => {
        if (!mountedRef.current) return;
        // fallback: assume setup complete so we don't misfire prompts
        setState({
          loading: false,
          groupCreated: true,
          memberAdded: true,
          setupComplete: true,
        });
      });
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchStatus();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchStatus]);

  return { ...state, refresh: fetchStatus };
}
